import { motion } from "framer-motion";

/**
 * Pure-CSS / framer-motion 3D scene that evokes solar panels + sun.
 * No external WebGL — works everywhere, stays performant.
 */
export default function Hero3D() {
  const cells = Array.from({ length: 16 });

  return (
    <div
      data-testid="hero-3d-scene"
      className="hero-scene relative aspect-square w-full max-w-[560px] mx-auto"
    >
      {/* Sun glow */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-[58%] h-[58%] rounded-full sun-ring glow-anim" />
      </div>

      {/* Rotating sun-ray ring */}
      <div className="absolute inset-0 flex items-center justify-center spin-anim">
        <svg viewBox="0 0 400 400" className="w-[78%] h-[78%]">
          {Array.from({ length: 24 }).map((_, i) => {
            const a = (i / 24) * Math.PI * 2;
            const x1 = 200 + Math.cos(a) * 150;
            const y1 = 200 + Math.sin(a) * 150;
            const x2 = 200 + Math.cos(a) * 180;
            const y2 = 200 + Math.sin(a) * 180;
            return (
              <line
                key={i}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="#EAB308"
                strokeWidth="2"
                strokeLinecap="round"
                opacity={i % 2 === 0 ? 0.85 : 0.35}
              />
            );
          })}
        </svg>
      </div>

      {/* Tilted solar panel */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center panel-3d float-anim"
        whileHover={{ scale: 1.04 }}
      >
        <div
          className="relative shadow-[0_30px_60px_-20px_rgba(20,20,30,0.35)] rounded-[14px]"
          style={{
            width: "60%",
            aspectRatio: "1.6 / 1",
            background:
              "linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)",
            border: "1.5px solid rgba(234,179,8,0.55)",
          }}
        >
          {/* Solar cells grid */}
          <div className="absolute inset-2 grid grid-cols-4 grid-rows-4 gap-1.5">
            {cells.map((_, i) => (
              <motion.div
                key={i}
                className="rounded-[3px]"
                style={{
                  background:
                    "linear-gradient(135deg, #1E40AF 0%, #1E3A8A 50%, #172554 100%)",
                  boxShadow:
                    "inset 0 0 0 1px rgba(255,255,255,0.06), inset 0 0 12px rgba(96,165,250,0.18)",
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 + i * 0.03 }}
              >
                <div
                  className="w-full h-full"
                  style={{
                    background:
                      "repeating-linear-gradient(0deg, transparent 0 6px, rgba(255,255,255,0.05) 6px 7px), repeating-linear-gradient(90deg, transparent 0 6px, rgba(255,255,255,0.05) 6px 7px)",
                  }}
                />
              </motion.div>
            ))}
          </div>

          {/* Reflective gloss */}
          <div
            className="absolute inset-0 rounded-[14px] pointer-events-none"
            style={{
              background:
                "linear-gradient(120deg, rgba(255,255,255,0.18) 0%, transparent 35%, transparent 65%, rgba(234,179,8,0.18) 100%)",
            }}
          />
        </div>
      </motion.div>

      {/* Floating stat chips */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="absolute top-6 left-2 md:left-6 bg-white/90 backdrop-blur-md border border-black/5 rounded-2xl px-4 py-3 shadow-lg"
        data-testid="hero-stat-yield"
      >
        <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">Ertrag/Jahr</div>
        <div className="font-display text-lg font-bold text-[#1A1D20]">
          11&apos;200 <span className="text-xs font-medium text-[#4B5563]">kWh</span>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="absolute bottom-8 right-2 md:right-4 bg-[#1A1D20] text-white rounded-2xl px-4 py-3 shadow-xl"
        data-testid="hero-stat-savings"
      >
        <div className="text-[10px] uppercase tracking-wider text-white/60">Einsparung</div>
        <div className="font-display text-lg font-bold">
          CHF 2&apos;480<span className="text-xs font-medium text-white/60">/Jahr</span>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="absolute bottom-1/3 -left-1 md:left-2 bg-[#EAB308] text-[#1A1D20] rounded-2xl px-3 py-2 shadow-lg"
        data-testid="hero-stat-co2"
      >
        <div className="text-[10px] uppercase tracking-wider text-[#1A1D20]/70">CO₂</div>
        <div className="font-display text-sm font-bold">−1.4 t/Jahr</div>
      </motion.div>
    </div>
  );
}
